import React from "react";
import { clsx } from "clsx";

import { Accordion as RadixAccordion } from "radix-ui";
import { ChevronDown } from "lucide-react";

import { twMerge } from "@/design-system/twMerge";

/**
 * Open/closed indicator. Exported so a trigger that needs the chevron
 * somewhere other than the leading edge can render it inside `showMore`
 * (pass `chevron={null}` to drop the built-in one). `className` overrides the
 * default rotation, e.g. `-rotate-90 group-data-[state=open]:rotate-0`.
 */
export function AccordionChevron({ className }: { className?: string }) {
  return (
    <ChevronDown
      size={20}
      className={twMerge(
        "shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180",
        className,
      )}
    />
  );
}

export interface AccordionItemProps {
  value: string;
  showMore: React.ReactNode;
  showLess?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  triggerProps?: RadixAccordion.AccordionTriggerProps;
  triggerPosition?: "top" | "bottom";
  contentProps?: RadixAccordion.AccordionContentProps;
  chevron?: React.ReactNode;
  /** Applied to the wrapper around `showMore`/`showLess`. */
  labelClassName?: string;
}

const AccordionItem = ({
  value,
  showMore,
  showLess,
  children,
  className,
  triggerProps,
  triggerPosition = "top",
  contentProps,
  chevron = <AccordionChevron />,
  labelClassName,
}: AccordionItemProps) => {
  const triggerElement = (
    <RadixAccordion.Header>
      <RadixAccordion.Trigger
        {...triggerProps}
        className={twMerge(
          clsx(
            "group text-label-sm flex w-full cursor-pointer items-center gap-2 py-3",
            "text-gray-900 transition-colors hover:text-gray-900/60 focus:outline-none focus-visible:ring-2",
            "focus-visible:ring-offset-2",
            triggerProps?.className,
          ),
        )}
      >
        {chevron}
        <span
          className={clsx(
            labelClassName,
            showLess && "group-data-[state=open]:hidden",
          )}
        >
          {showMore}
        </span>
        {showLess && (
          <span
            className={clsx(labelClassName, "group-data-[state=closed]:hidden")}
          >
            {showLess}
          </span>
        )}
      </RadixAccordion.Trigger>
    </RadixAccordion.Header>
  );
  const contentElement = (
    <RadixAccordion.Content
      {...contentProps}
      className={twMerge(
        clsx(
          "data-[state=closed]:animate-radixAccordionClose text-body-sm overflow-hidden py-3",
          "data-[state=open]:animate-radixAccordionOpen",
          contentProps?.className,
        ),
      )}
    >
      {children}
    </RadixAccordion.Content>
  );
  return (
    <RadixAccordion.Item
      value={value}
      className={twMerge(clsx("border-gray-200", className))}
    >
      {triggerPosition === "top" ? (
        <>
          {triggerElement}
          {contentElement}
        </>
      ) : (
        <>
          {contentElement}
          {triggerElement}
        </>
      )}
    </RadixAccordion.Item>
  );
};

export interface AccordionSingleProps {
  className?: string;
  children: React.ReactNode;
  type?: "single";
  collapsible?: boolean;
  rootProps?: Partial<RadixAccordion.AccordionSingleProps>;
}

export interface AccordionMultipleProps {
  className?: string;
  children: React.ReactNode;
  type: "multiple";
  rootProps?: Partial<RadixAccordion.AccordionMultipleProps>;
}

export type AccordionProps = AccordionSingleProps | AccordionMultipleProps;
export function Accordion(props: AccordionProps) {
  const { className, children, rootProps } = props;

  if (props.type === "single") {
    return (
      <RadixAccordion.Root
        type="single"
        {...(rootProps as Partial<RadixAccordion.AccordionSingleProps>)}
        collapsible={props.collapsible ?? true}
        className={twMerge(clsx("divide-y", className))}
      >
        {children}
      </RadixAccordion.Root>
    );
  }

  return (
    <RadixAccordion.Root
      type="multiple"
      {...(rootProps as Partial<RadixAccordion.AccordionMultipleProps>)}
      className={twMerge(clsx("divide-y", className))}
    >
      {children}
    </RadixAccordion.Root>
  );
}

Accordion.Item = AccordionItem;
